import moment from "moment";
import Teachr from "../../../Objects/models/Teachr";
import { PunctualCourseDashboard } from "../../../Objects/models/PunctualCourseDashboard";
import CourseProposal from "../../../Objects/models/CourseProposal";
import { APIErrorEnum } from "../../../Objects/enums/APIErrorEnum";
import PunctualCourseRepository from "../../../Repositories/PunctualCourseRepository";
import CourseProposalRepository from "../../../Repositories/CourseProposalRepository";
import TravelRepository from "../../../Repositories/TravelRepository";
import TransportSubscriptionRepository from "../../../Repositories/TransportSubscriptionRepository";
import { ENV_DECLARONS, ENV_VERIFIED_TRAINER, isBrandActive } from "../../../config/environment.config";
import Functions from "../../../Helpers/Functions";
import { sessionsToApiPayload } from "../../DeclarationCourse/SessionList";
import { describeOverlapApiError } from "../../DeclarationCourse/overlapMessages";
import TravelStore from "../../DeclarationCourse/Travel/TravelStore";
import { vocab } from "../vocabulary";
import { DeclarationV2State } from "../types";
import { declarationHours, declarationPeriod } from "../summary";
import { isRelationTeachrChild, relationHourlyRate } from "../components/relationTeachr";

/**
 * Envoi d'une déclaration : copie de la chaîne de l'ancien parcours
 * (Utils/Function : handleSubmitCourse → handleProposal →
 * handleCreateCourseProposal → handleCreateTryToAccept), sans effets Redux :
 * 1. création du cours (PunctualCourseDashboard), avec séances, acompte,
 *    attestation, preuve et déroulement propre au cours ;
 * 2. preuve de déplacement (> 160 km) ou abonnement de transport rattachés au
 *    cours, sans bloquer en cas d'échec ;
 * 3. proposition du prof puis acceptation : le cours passe en attente de
 *    validation par le client (48 h).
 * Renvoie l'identifiant du cours créé ; lève une erreur avec un message
 * déjà traduit pour l'écran.
 */
export class DeclarationError extends Error {}

const CREATION_MESSAGES: Record<string, string> = {
    teachr_account_not_active: "Votre compte n'est pas encore actif : terminez votre profil (statut auto-entrepreneur, conditions de service signées, code de validation) avant de déclarer.",
    teachr_iban_missing: "Renseignez votre IBAN dans votre compte avant de déclarer.",
    teachr_address_missing: "Renseignez votre adresse dans votre compte avant de déclarer.",
    [APIErrorEnum.PUNCTUAL_COURSE_RANGE_NOT_CORRESPONDING]: "La durée est supérieure à la plage horaire définie.",
    [APIErrorEnum.PUNCTUAL_COURSE_CLIENT_MUST_HAVE_AT_LEAST_ONE_ADDRESS]: "Vous devez définir au moins une adresse dans votre compte.",
    [APIErrorEnum.COURSE_PROPOSAL_TEACHR_HAS_ALREADY_A_COURSE_AT_THIS_TIME]: "Vous avez déjà un cours prévu à cette date.",
    [APIErrorEnum.PUNCTUAL_COURSE_PENDING_RESERVATIONS_LIMIT_REACHED]: "Vous avez déjà trop de demandes en attente.",
    [APIErrorEnum.PUNCTUAL_COURSE_DURATION_LIMIT_MAX]: "La durée ne peut pas dépasser 12 heures.",
    [APIErrorEnum.PUNCTUAL_COURSE_DURATION_LIMIT_MIN]: "La durée ne peut pas être inférieure à 1 heure.",
    [APIErrorEnum.PUNCTUAL_COURSE_DIRECT_COURSE_NOT_AUTHORIZED]: "Vous n'êtes pas autorisé à déclarer directement pour ce client.",
    [APIErrorEnum.PUNCTUAL_COURSE_MAX_COURSE_FOR_THE_MONTH]: "La limite de 8 cours par mois est atteinte pour ce client. Attendez le mois suivant ou contactez-nous.",
    child_not_found: "Le client sélectionné ne fait plus partie de votre liste. Choisissez-en un autre.",
    deposit_attestation_required: "Vous devez certifier sur l'honneur l'attestation d'acompte avant de valider.",
    proof_deposit_required: "Vous devez joindre une preuve d'acompte (PDF, PNG ou JPEG) avant de valider.",
    deposit_attestation_failed: "La génération de l'attestation d'acompte a échoué. Votre déclaration n'a pas été enregistrée, veuillez réessayer.",
    deposit_locked_urssaf: "L'acompte a déjà été transmis à l'URSSAF et ne peut plus être modifié.",
    session_description_required: "Vous devez d'abord renseigner le déroulement habituel de vos cours dans Paramètres, onglet Descriptions.",
};

const PROPOSAL_MESSAGES: Record<string, string> = {
    [APIErrorEnum.COURSE_PROPOSAL_TEACHR_HAS_ALREADY_A_COURSE_AT_THIS_TIME]: "Vous avez déjà un cours prévu pour ce créneau horaire.",
    [APIErrorEnum.COURSE_PROPOSAL_ALREADY_APPLIED]: "Une proposition existe déjà pour ce cours.",
    [APIErrorEnum.COURSE_PROPOSAL_DURATION_NOT_CORRESPOND]: "La durée ne correspond pas à la plage horaire définie.",
    "This value should be between 6 and 250.": "Le prix par heure doit se situer entre 11 et 250 euros.",
    "This value should be between 5 and 23.": "Le prix par heure doit se situer entre 9 et 23 euros.",
    "The end datetime must be within the next month.": "La déclaration ne doit pas excéder une durée d'un mois.",
    "The start datetime must be in the future.": "La date et l'heure choisies sont déjà passées.",
    "The end datetime must be in the future.": "La date et l'heure choisies sont déjà passées.",
    deposit_attestation_required: "Vous devez certifier sur l'honneur l'attestation d'acompte avant de valider.",
    proof_deposit_required: "Vous devez joindre une preuve d'acompte (PDF, PNG ou JPEG) avant de valider.",
};

const GENERIC_MESSAGE = `Une erreur est survenue. Nous vous invitons à recommencer la déclaration ${vocab.ofTheCourse()}. Si le problème persiste, déconnectez-vous puis reconnectez-vous.`;

const describeApiError = (error: any, messages: Record<string, string>): string => {
    const description: string | undefined = error?._description ?? error?.description;
    const attributes: Array<{ description?: string }> = error?._arrayInvalidAttributes ?? [];
    const fromAttributes = attributes.map((a) => messages[a?.description ?? ""] ?? describeOverlapApiError(a?.description)).find(Boolean);
    return fromAttributes || (description && (messages[description] || describeOverlapApiError(description))) || GENERIC_MESSAGE;
};

/** Objet envoyé à l'API, construit depuis l'état du parcours (même forme que LastStep). */
export const buildCourse = (state: DeclarationV2State, teachr: Teachr): PunctualCourseDashboard => {
    const child = state.student?.child;
    const multiple = state.courseType === "multiple";
    const { start, end } = declarationPeriod(state);
    const hours = declarationHours(state);
    const price = state.pricing.price;
    // relation Teach'r : l'API reprend le tarif fixé (teachrPrice = pricePerHour) ; on l'envoie tel quel,
    // sans la formule 2 × prix × (1 − frais) des contacts du prof
    const teachrPrice = isRelationTeachrChild(child)
        ? relationHourlyRate(child).toFixed(2)
        : Functions.priceTotal(parseFloat(price) || 0, parseFloat((teachr as any)?.defaultFees ?? "0") || 0).toFixed(2);
    const isOver = !!end && moment(end).isBefore(moment());
    const subjectId = isBrandActive(ENV_VERIFIED_TRAINER) ? 108 : state.pricing.subject?.id;

    const course = new PunctualCourseDashboard(
        child?.childId,
        isOver ? "over" : "",
        child?.addressId,
        subjectId,
        start ?? "",
        end ?? "",
        false,
        teachr?.id,
        false,
        (hours * 2).toString(),
        true,
        false,
        "",
        true,
        "",
        state.note ?? "",
        multiple
    );
    course.isPassedCourse = false;
    course.brand = isBrandActive(ENV_DECLARONS) ? "declarons" : undefined;
    course.sessions = multiple ? sessionsToApiPayload(state.sessions) : null;
    course.teachrPrice = teachrPrice as any;
    course.sessionDescriptionOverride = state.sessionDescriptionOverride ?? null;

    const deposit = state.pricing.deposit;
    if (deposit.enabled && parseFloat(deposit.amount) > 0) {
        course.deposit = parseFloat(deposit.amount);
        course.depositDate = deposit.date ? moment(deposit.date).toDate() : new Date();
        course.depositAttestationAccepted = deposit.attestationAccepted === true;
        if (deposit.proof?.id) {
            if (multiple) course.proofDeposits = [deposit.proof.id];
            else course.proofDeposit = deposit.proof.id;
        }
    }
    return course;
};

/**
 * Envoie un cours déjà construit (création, preuve de déplacement, proposition,
 * acceptation) et renvoie son identifiant. Partagé avec les brouillons v2, qui
 * construisent leur cours depuis un cours du mois précédent.
 */
export const submitBuiltCourse = async (course: PunctualCourseDashboard, clientId: number | undefined, teachr: Teachr): Promise<{ courseId: number }> => {
    let created: any;
    try {
        created = await PunctualCourseRepository.createPunctualCourseByTeacher(course);
    } catch (error: any) {
        throw new DeclarationError(describeApiError(error, CREATION_MESSAGES));
    }
    if (!created?.id) throw new DeclarationError(GENERIC_MESSAGE);

    // Preuve de déplacement ou abonnement de transport : jamais bloquant.
    const proofFile = TravelStore.getProofFile(clientId);
    if (proofFile) {
        try {
            await TravelRepository.uploadTravelProof(created.id, proofFile);
            TravelStore.setProofFile(null);
        } catch (e) {
            console.warn("Upload de la preuve de déplacement échoué", e);
        }
    } else if (TravelStore.getUseSubscription(clientId)) {
        try {
            await TransportSubscriptionRepository.attachToCourse(created.id);
        } catch (e) {
            console.warn("Rattachement de l'abonnement de transport échoué", e);
        }
    }

    const proposal = new CourseProposal(
        (teachr as any)["@id"],
        `/punctual_courses/${created.id}`,
        course.startDateTime ?? "",
        course.endDateTime ?? "",
        String(course.teachrPrice ?? ""),
        null
    );
    proposal.isFromTeachr = true;
    let proposalId: number | undefined;
    try {
        const createdProposal: any = await CourseProposalRepository.createCourseProposal(proposal);
        proposalId = createdProposal?.id;
    } catch (error: any) {
        throw new DeclarationError(describeApiError(error, PROPOSAL_MESSAGES));
    }
    if (!proposalId) throw new DeclarationError(GENERIC_MESSAGE);

    try {
        const accepted = await PunctualCourseRepository.tryAcceptPunctualCourseByTeachr(clientId as number, proposalId);
        if (!accepted || typeof accepted !== "object") throw new Error("not accepted");
    } catch (error: any) {
        throw new DeclarationError(describeApiError(error, PROPOSAL_MESSAGES));
    }
    return { courseId: created.id };
};

export const submitDeclaration = async (state: DeclarationV2State, teachr: Teachr): Promise<{ courseId: number }> =>
    submitBuiltCourse(buildCourse(state, teachr), state.student?.child?.clientId, teachr);

export default submitDeclaration;
